import React, { FC } from 'react';

import { DropdownOption } from 'types';

import _ from '../locale';
import { styled } from '../theme';
import InputComment from './InputComment';
import Radio from './Radio';

const StyledRadio = styled(Radio)({
    marginRight: 20,
    paddingBottom: 10,
});

type Props = {
    name: string;
    value: string | boolean | number;
    options: DropdownOption[];
    onChange: (ev: { target: { value: any } }) => void;
    onCommentChange?: (ev: { target: { value: any } }) => void;
    comments?: Record<number, string>;
    onPaste?: (e: React.ClipboardEvent<HTMLInputElement>) => void;
    pasteWarnings?: Record<number, boolean>;
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
};

/**
 * Component
 *
 * @example
 * ```
 * <RadioSelect
 name={'name is mandatory'}
 value={var}
 onChange={(ev) => {
            const { value } = ev.target;
            var = value
        }}
 options={[
            { value: false, label: _.get('news_and_announcements_publish_option_1') },
            { value: true, label: _.get('news_and_announcements_publish_option_2') },
        ]}
 />
 * ```
 */
const RadioSelect: FC<Props> = ({
    name,
    value,
    options,
    comments,
    onChange,
    onCommentChange,
    onPaste,
    pasteWarnings,
    onBlur,
    ...restProps
}) => (
    <div {...restProps}>
        {options.map((option) => (
            <StyledRadio
                key={option?.value?.toString()}
                name={name}
                value={option?.value}
                checked={value === option?.value}
                onChange={({ target }) => {
                    const value =
                        target.value === 'true'
                            ? true
                            : target.value === 'false'
                              ? false
                              : target.value;
                    onChange({ target: { name, value } });
                }}
            >
                {option?.label}
                {option?.commentable && (
                    <InputComment
                        name={option.value.toString()}
                        value={comments?.[option.value] || ''}
                        onChange={onCommentChange}
                        onPaste={onPaste}
                        showWarning={pasteWarnings?.[option.value]}
                        onBlur={onBlur}
                    />
                )}
            </StyledRadio>
        ))}
    </div>
);

export default RadioSelect;
